/**
 * Input Component
 *
 * Reusable text input with consistent styling
 * - Multiple types (text, email, password, number, url, tel)
 * - Error state
 * - Disabled state
 * - Icon support (left or right)
 * - Size variants
 */

import React from "react";
import PropTypes from "prop-types";
import "./Input.scss";

export const Input = React.forwardRef(({
  type = "text",
  value,
  onChange,
  placeholder,
  disabled = false,
  error = false,
  icon,
  iconPosition = "left",
  size = "medium",
  fullWidth = false,
  className = "",
  ...props
}, ref) => {
  const inputClasses = [
    "input",
    `input--${size}`,
    error && "input--error",
    disabled && "input--disabled",
    icon && `input--with-icon-${iconPosition}`,
    fullWidth && "input--full-width",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const wrapperClasses = [
    "input-wrapper",
    fullWidth && "input-wrapper--full-width",
  ]
    .filter(Boolean)
    .join(" ");

  if (icon) {
    return (
      <div className={wrapperClasses}>
        {icon && iconPosition === "left" && (
          <span className="input__icon input__icon--left">{icon}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={inputClasses}
          ref={ref}
          {...props}
        />
        {icon && iconPosition === "right" && (
          <span className="input__icon input__icon--right">{icon}</span>
        )}
      </div>
    );
  }

  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={inputClasses}
      ref={ref}
      {...props}
    />
  );
});

Input.displayName = "Input";

Input.propTypes = {
  type: PropTypes.oneOf([
    "text",
    "email",
    "password",
    "number",
    "url",
    "tel",
    "search",
    "date",
  ]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(["left", "right"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
};

export default Input;
